export const APP_CONFIG = {
  BASE_PATH: '/hotmath',
  DESCRIPTION:
    'Interactive Learning Platform for Math Education. Practice problems, get help with homework, and master math concepts.',
  TITLE: 'HotMath - Interactive Learning Platform',
} as const;

// Feature flags
export const FEATURE_FLAGS = {
  DISPLAY_VIDEOS: true, // Set to true to re-enable video display
} as const;

export const DIFFICULTY_LEVELS = {
  ADVANCED: 'Advanced',
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
} as const;

export const DIFFICULTY_COLORS = {
  [DIFFICULTY_LEVELS.ADVANCED]:
    'bg-semantic-error/10 text-semantic-error border border-semantic-error/30',
  [DIFFICULTY_LEVELS.BEGINNER]:
    'bg-semantic-success/10 text-semantic-success border border-semantic-success/30',
  [DIFFICULTY_LEVELS.INTERMEDIATE]:
    'bg-semantic-warning/10 text-semantic-warning border border-semantic-warning/30',
} as const;

// Exercise type semantic mappings
export const EXERCISE_TYPE_COLORS = {
  CHOICE: 'exercise-choice',
  QUESTION: 'exercise-question',
  THINKING: 'exercise-thinking',
} as const;

export const EXERCISE_GRADIENTS = {
  CHOICE: 'from-exercise-choice to-exercise-choice/80',
  QUESTION: 'from-exercise-question to-exercise-question/80',
  THINKING: 'from-exercise-thinking to-exercise-thinking/80',
} as const;

export const PROBLEM_TYPES = {
  QUESTION_EXERCISE: 'questionExercise',
  SINGLE_CHOICE_EXERCISE: 'singleChoiceExercise',
  THINKING_EXERCISE: 'thinkingExercise',
} as const;

export const ANSWER_TYPES = {
  SIMPLE: 'simple',
  STEPS: 'steps',
} as const;

export const EXPLANATION_LEVELS = {
  EASY: 'easy',
  HARD: 'hard',
  MEDIUM: 'medium',
} as const;

export const PROGRESS_INDICATORS = [
  {
    colors: 'glass-card',
    icon: 'BookOpen',
    iconColor: 'text-semantic-success',
    subtitle: 'Get the basics',
    subtitleColor: 'text-secondary',
    title: 'Learn',
  },
  {
    colors: 'glass-card',
    icon: 'Target',
    iconColor: 'text-brand-primary',
    subtitle: 'Apply your skills',
    subtitleColor: 'text-secondary',
    title: 'Practice',
  },
  {
    colors: 'glass-card',
    icon: 'Trophy',
    iconColor: 'text-semantic-warning',
    subtitle: 'Achieve excellence',
    subtitleColor: 'text-secondary',
    title: 'Master',
  },
] as const;

export const SIDEBAR_SECTIONS = {
  NOTATION: 'notation',
  NOTES: 'notes',
  QUICK_REFERENCE: 'quickReference',
  REMEMBER: 'remember',
  THEOREM: 'theorem',
  WHY_IT_WORKS: 'whyItWorks',
} as const;

export const SIDEBAR_SECTION_CONFIGS = {
  [SIDEBAR_SECTIONS.NOTATION]: {
    colors: 'from-semantic-info/10 to-semantic-info/5 border-semantic-info/30',
    icon: '📝',
    iconColor: 'text-semantic-info',
    textColor: 'text-semantic-info',
  },
  [SIDEBAR_SECTIONS.NOTES]: {
    bulletColor: 'text-semantic-success',
    colors:
      'from-semantic-success/10 to-semantic-success/5 border-semantic-success/30',
    icon: '💡',
    iconColor: 'text-semantic-success',
    textColor: 'text-semantic-success',
  },
  [SIDEBAR_SECTIONS.QUICK_REFERENCE]: {
    colors:
      'from-semantic-error/10 to-semantic-error/5 border-semantic-error/30',
    icon: '⚡',
    iconColor: 'text-semantic-error',
    labelColor: 'text-semantic-error',
    textColor: 'text-semantic-error',
  },
  [SIDEBAR_SECTIONS.REMEMBER]: {
    colors:
      'from-semantic-warning/10 to-semantic-warning/5 border-semantic-warning/30',
    icon: '⭐',
    iconColor: 'text-semantic-warning',
    textColor: 'text-semantic-warning',
  },
  [SIDEBAR_SECTIONS.THEOREM]: {
    colors:
      'from-brand-secondary/10 to-brand-secondary/5 border-brand-secondary/30',
    icon: '🔑',
    iconColor: 'text-brand-secondary',
    textColor: 'text-brand-secondary',
  },
  [SIDEBAR_SECTIONS.WHY_IT_WORKS]: {
    colors: 'from-brand-primary/10 to-brand-primary/5 border-brand-primary/30',
    icon: '🧠',
    iconColor: 'text-brand-primary',
    textColor: 'text-brand-primary',
  },
} as const;

export const ROUTES = {
  CONCEPT_DETAIL: '/hotmath_help/topics',
  CONCEPT_LIST: '/',
  HOME: '/hotmath',
} as const;

export const ERROR_MESSAGES = {
  CONCEPT_NOT_FOUND: 'Concept not found',
  FAILED_TO_LOAD: 'Failed to load concept content',
  UNKNOWN_ERROR: 'Unknown error',
} as const;

// Type definitions for better type safety
export type DifficultyLevel =
  (typeof DIFFICULTY_LEVELS)[keyof typeof DIFFICULTY_LEVELS];
export type ProblemType = (typeof PROBLEM_TYPES)[keyof typeof PROBLEM_TYPES];
export type AnswerType = (typeof ANSWER_TYPES)[keyof typeof ANSWER_TYPES];
export type ExplanationLevel =
  (typeof EXPLANATION_LEVELS)[keyof typeof EXPLANATION_LEVELS];
export type SidebarSection =
  (typeof SIDEBAR_SECTIONS)[keyof typeof SIDEBAR_SECTIONS];
export type RouteKey = (typeof ROUTES)[keyof typeof ROUTES];

// Exercise color mappings using semantic tokens
export const EXERCISE_COLORS = {
  BLUE: {
    background: 'bg-brand-primary',
    hover: 'hover:bg-brand-primary/90',
    text: 'text-white',
  },
  EMERALD: {
    background: 'bg-semantic-success',
    hover: 'hover:bg-interactive-success',
    text: 'text-white',
  },
  GREEN: {
    background: 'bg-semantic-success',
    hover: 'hover:bg-interactive-success',
    text: 'text-white',
  },
  PURPLE: {
    background: 'bg-semantic-info',
    hover: 'hover:bg-interactive-info',
    text: 'text-white',
  },
  RED: {
    background: 'bg-semantic-error',
    hover: 'hover:bg-interactive-error',
    text: 'text-white',
  },
  YELLOW: {
    background: 'bg-semantic-warning',
    hover: 'hover:bg-interactive-warning',
    text: 'text-white',
  },
} as const;
